import type { ApolloClient, NormalizedCacheObject } from '@apollo/client';

import { CATEGORY_GROUPS_NUMBER } from 'src/core/domain/constants';
import {
  SeekMaxCategoryGroupsDocument,
  SetSeekMaxCategoryGroupFollowedStatusDocument,
  type SetSeekMaxCategoryGroupFollowedStatusMutationVariables,
  type SeekMaxCategoryGroupsQuery,
  type SeekMaxCategoryGroupsQueryVariables,
  type SeekMaxCategoryGroupQueryVariables,
  SeekMaxCategoryGroupDocument,
} from 'src/generated/gql/graphql';
import {
  type CategoryGroupsLoaderData,
  mappingGroup,
  mappingGroups,
} from 'src/mapper/groupMapper';
import { client as csrClient } from 'src/modules/apollo/apolloClient';
import logger from 'src/modules/logger/logger';
import type {
  CareerHubLanguage,
  ConfigStateContext,
} from 'src/types/ConfigStateContext';

export interface fetchAllGroupsParams {
  config: Pick<ConfigStateContext, 'zone' | 'language'>;
  hasLearningProfile: boolean;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const fetchAllGroups = async ({
  config,
  hasLearningProfile = false,
  client = csrClient,
}: fetchAllGroupsParams): Promise<CategoryGroupsLoaderData[]> => {
  try {
    const allGroups: CategoryGroupsLoaderData[] = [];
    let cursor = 0;
    let hasNextPage = true;

    while (hasNextPage) {
      const variables: SeekMaxCategoryGroupsQueryVariables = {
        zone: config.zone,
        cursor,
        pageSize: CATEGORY_GROUPS_NUMBER,
        hasLearningProfile,
      };

      logger.info('fetchCategoryGroups', variables);

      const { data } = await client.query<
        SeekMaxCategoryGroupsQuery,
        SeekMaxCategoryGroupsQueryVariables
      >({
        query: SeekMaxCategoryGroupsDocument,
        variables,
      });

      allGroups.push(
        ...mappingGroups(
          data.seekMaxCategoryGroups,
          config.language as CareerHubLanguage,
        ),
      );
      if (!data.seekMaxCategoryGroups?.pageInfo?.hasNextPage) {
        hasNextPage = false;
      } else {
        cursor += CATEGORY_GROUPS_NUMBER;
      }
    }
    return allGroups;
  } catch (error) {
    logger.error({ error }, 'fetchCategoryGroups');
    throw error;
  }
};

export interface fetchGroupParams {
  config: Pick<ConfigStateContext, 'zone' | 'language'>;
  groupId: string;
  hasLearningProfile: boolean;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const fetchGroup = async ({
  config,
  groupId,
  hasLearningProfile = false,
  client = csrClient,
}: fetchGroupParams): Promise<CategoryGroupsLoaderData> => {
  try {
    const variables: SeekMaxCategoryGroupQueryVariables = {
      zone: config.zone,
      groupId,
      hasLearningProfile,
    };

    logger.info('fetchCategoryGroup', variables);

    const { data } = await client.query({
      query: SeekMaxCategoryGroupDocument,
      variables,
    });
    const group = mappingGroup(
      data.seekMaxCategoryGroup,
      config.language as CareerHubLanguage,
    );
    return group;
  } catch (error) {
    logger.error({ error }, 'fetchCategoryGroup');
    throw error;
  }
};

export interface updateFollowCategoryStatusParams {
  groupId: string;
  followed: boolean;
  client?: ApolloClient<NormalizedCacheObject>;
}
export const updateFollowCategoryStatus = async ({
  groupId,
  followed,
  client = csrClient,
}: updateFollowCategoryStatusParams): Promise<any> => {
  try {
    const variables: SetSeekMaxCategoryGroupFollowedStatusMutationVariables = {
      input: {
        groupId,
        followed,
      },
    };

    logger.info('updateFollowCategoryStatus', variables);

    const { data } = await client.mutate({
      mutation: SetSeekMaxCategoryGroupFollowedStatusDocument,
      variables,
    });

    return data?.setSeekMaxCategoryGroupFollowedStatus;
  } catch (error) {
    logger.error({ error }, 'updateFollowCategoryStatus');
    throw error;
  }
};
